"use client";

import { Minus, Plus } from "lucide-react";
import { Button } from "@/shadcn-components/ui/button";
import { Input } from "@/shadcn-components/ui/input";
import { Label } from "@/shadcn-components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shadcn-components/ui/select";

interface IndicatorParametersProps {
  parameters?: Array<{
    name: string;
    label?: string;
    type: "number" | "color" | "select";
    defaultValue: number | string;
    options?: string[];
    min?: number;
    max?: number;
    step?: number;
  }>;
  values: Record<string, number | string>;
  onValueChange: (name: string, value: number | string) => void;
}

function clamp(n: number, min?: number, max?: number) {
  let v = n;
  if (min != null) v = Math.max(min, v);
  if (max != null) v = Math.min(max, v);
  return v;
}

function NumberStepper({
  label,
  value,
  min,
  max,
  step = 1,
  decimals,
  onChange,
}: {
  label: string;
  value: number;
  min?: number;
  max?: number;
  step?: number;
  decimals?: number;
  onChange: (v: number) => void;
}) {
  const display =
    decimals != null ? value.toFixed(decimals) : String(value);

  const adjust = (delta: number) => {
    const next = clamp(
      Number((value + delta).toFixed(decimals ?? 4)),
      min,
      max
    );
    onChange(next);
  };

  return (
    <div className="flex items-center justify-between gap-3 py-2.5 border-b border-border/60 last:border-0">
      <Label className="text-sm font-normal text-foreground shrink-0">
        {label}
      </Label>
      <div className="flex items-center gap-1">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={() => adjust(-step)}
          disabled={min != null && value <= min}
          aria-label={`Decrease ${label}`}
        >
          <Minus className="h-3.5 w-3.5" />
        </Button>
        <Input
          type="number"
          value={display}
          onChange={(e) => {
            const parsed = parseFloat(e.target.value);
            if (!Number.isNaN(parsed)) {
              onChange(clamp(parsed, min, max));
            }
          }}
          min={min}
          max={max}
          step={step}
          className="h-8 w-[4.5rem] text-center text-sm tabular-nums px-1"
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={() => adjust(step)}
          disabled={max != null && value >= max}
          aria-label={`Increase ${label}`}
        >
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

export default function IndicatorParameters({
  parameters,
  values,
  onValueChange,
}: IndicatorParametersProps) {
  if (!parameters?.length) {
    return (
      <p className="text-sm text-muted-foreground py-3 text-center">
        No parameters for this indicator.
      </p>
    );
  }

  return (
    <div>
      {parameters.map((param) => {
        const displayLabel = param.label ?? param.name;
        if (param.type === "number") {
          const isDecimal =
            param.name === "Deviations" ||
            param.name === "Deviation" ||
            (param.step != null && param.step < 1);
          return (
            <NumberStepper
              key={param.name}
              label={displayLabel}
              value={Number(values[param.name] ?? param.defaultValue)}
              min={param.min}
              max={param.max}
              step={param.step ?? (isDecimal ? 0.001 : 1)}
              decimals={isDecimal ? 3 : undefined}
              onChange={(v) => onValueChange(param.name, v)}
            />
          );
        }

        if (param.type === "select" && param.options) {
          return (
            <div
              key={param.name}
              className="flex flex-col gap-1.5 py-2.5 border-b border-border/60 last:border-0"
            >
              <Label className="text-sm font-normal text-foreground">
                {displayLabel}
              </Label>
              <Select
                value={String(values[param.name] ?? param.defaultValue)}
                onValueChange={(v) => onValueChange(param.name, v)}
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-56">
                  {param.options.map((opt) => (
                    <SelectItem key={opt} value={opt} className="text-sm">
                      {opt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        }

        return null;
      })}
    </div>
  );
}
